//import { projection } from '../projections/epgsg-21781.js';
import { projection } from '../projections/epgsg-3857.js';
import { Fill, Stroke, Style } from 'ol/style';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import MVT from 'ol/format/MVT';
import { config } from 'config';
import chroma from 'chroma-js';
import { buffer } from 'ol/extent.js';
import { relief } from './relief.js';
import { fhr } from '../utils/fhr.js';

export { Fill, Stroke, Style };

/**
 *
 * @module layers/region
 * @type {Object}
 */
export const region = {

  /**
   * The layer containing the polygons.
   * @type {VectorTileLayer}
   */
  layer: null,

  /**
   * @type {Style}
   */
  _style: {},

  _url: config.service.geo + '/geometry/extent',

  /**
   * the default stroke style
   */
  stroke: new Stroke({
    color: config.color.region.stoke,
    width: 1
  }),

  /**
   * the highlighted stroke style
   */
  highlightedStroke: new Stroke({
    color: config.color.highlight,
    width: 2
  }),

  /**
   * Creates the region layer.
   * @param {JsonStatReader} jsonStatReader
   * @param {DataSeries} rowData
   * @returns {region}
   */
  init (jsonStatReader, rowData) {
    const varNr = jsonStatReader.getRegionId();
    this._reader = jsonStatReader;
    this.layer = this._get(varNr, rowData);
    return this;
  },

  /**
   * Create and return the region layer.
   * @returns {VectorTileLayer}
   * @param {String} varNr variable number
   * @param {DataSeries} rowData
   */
  _get (varNr, rowData) {
    return new VectorTileLayer({
      source: new VectorTileSource({
        format: new MVT({ idProperty: 'regionid' }),
        url: config.service.geo + '/tile/' + varNr + '/{z}/{x}/{y}.pbf',
      }),
      extent: buffer(projection.extent, relief.BUFFER_SIZE),
      zIndex: config.layerZIndex.region,
      style: feature => this._getStyleArea(feature, rowData)
    });
  },

  /**
   * Update the area style of the polygons.
   * @param {DataSeries} rowData
   */
  update (rowData) {
    this.layer.setStyle(feature => {
      return this._getStyleArea(feature, rowData);
    });
  },

  /**
   * Return the style for the area of the polygons.
   * @return {Style}
   * @private
   * @param {Feature} feature
   * @param {DataSeries} rowData
   */
  _getStyleArea (feature, rowData) {
    const id = feature.getId();

    const style = this._getStyle(feature, rowData);
    this._style[id] = style;

    return style;
  },

  /**
   * Create the area style of the region.
   * @returns {Style}
   * @private
   * @param {Feature} feature
   * @param {DataSeries} rowData
   */
  _getStyle (feature, rowData) {
    // Note: When a region was excluded (by a restriction), it's also excluded from the JSON-stat.
    //    Thus, find() will not find a matching region and return undefined.
    const item = rowData.series.find((region) => parseInt(region.id) === feature.getId());
    return new Style({
      fill: new Fill({
        color: this._getColor(item)
      }),
      stroke: this.stroke
    });
  },

  /**
   * Return the color for a region.
   * @param {DataSeriesItem} item
   * @return {string}
   * @private
   */
  _getColor (item) {
    let color;
    if (item === undefined) {
      color = chroma(config.color.region.excluded).alpha(config.color.region.opacity).hex();
    } else {
      color = item.color;
    }

    return color;
  },

  /**
   * Update the border style of the polygons.
   * When null is passed, the border style
   * is set to the default (not-highlighted) style.
   * @param {BaseEvent} evt
   * @param {Feature} [hoveredFeature]
   */
  updateStyleBorder (evt, hoveredFeature = null) {
    this.layer.setStyle(feature => {
      return this._updateStroke(feature, hoveredFeature);
    });
  },

  /**
   * Update the stroke style of the polygons.
   * @param feature
   * @param hoveredFeature
   * @return {*}
   * @private
   */
  _updateStroke (feature, hoveredFeature) {
    let style = this._style[feature.getId()];

    if (hoveredFeature && hoveredFeature.getId() === feature.getId()) {
      style.setStroke(this.highlightedStroke);
      style.setZIndex(config.layerZIndex.water + 1);
    } else {
      style.setStroke(this.stroke);
      style.setZIndex(config.layerZIndex.region);
    }

    return style;
  },

  fetchExtent (varNr) {
    return fetch(this._url + '/' + varNr).then(fhr.json);
  }
};